<template>
  <!--
    Plan 091 — fan-visible carousel controls for the QUESTS / REWARDS rails.

    Two circular, theme-adaptive (V2 `fill-brand`) arrow buttons that scroll the
    associated horizontal rail one page left / right. The Figma desktop frame
    (Monaco, red theme) shows these as red circular ‹ › buttons at the top-right
    of both the QUESTS and REWARDS section headers, visible to every viewer (not
    just hosts). They sit alongside the host-only manage/+ action.

    `railEl` is the rail's scrollable container element (the `$el` of
    `ChannelRailVirtualized`, which binds `useVirtualList`'s `containerProps` —
    an `overflow-x-auto` div — to its root). We read it lazily on each click so
    the component works whether the ref resolves to a DOM node or a component
    instance.

    Edge state: arrows disable at the start / end of the scrollable range and
    re-evaluate on scroll and resize. When the rail has no overflow (empty or
    short) BOTH arrows are disabled, matching "nothing to scroll".
  -->
  <div class="flex flex-row gap-2 items-center">
    <button
      type="button"
      class="rounded-full size-10 flex items-center justify-center bg-fill-brand text-text-on-brand transition-opacity hover:bg-fill-brand-hover disabled:opacity-40 disabled:cursor-default disabled:hover:bg-fill-brand"
      :disabled="!canScrollLeft"
      :aria-label="t('channel.rail.scrollPrevious')"
      @click="scrollByPage(-1)"
    >
      <Icon
        class="size-6"
        icon="material-symbols:arrow-back-rounded"
        aria-hidden="true"
      />
    </button>

    <button
      type="button"
      class="rounded-full size-10 flex items-center justify-center bg-fill-brand text-text-on-brand transition-opacity hover:bg-fill-brand-hover disabled:opacity-40 disabled:cursor-default disabled:hover:bg-fill-brand"
      :disabled="!canScrollRight"
      :aria-label="t('channel.rail.scrollNext')"
      @click="scrollByPage(1)"
    >
      <Icon
        class="size-6"
        icon="material-symbols:arrow-forward-rounded"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  /**
   * The rail. Either the scrollable DOM element directly, or the
   * `ChannelRailVirtualized` component instance (whose `$el` is the scrollable
   * `overflow-x-auto` container). Resolved lazily via `resolveScrollEl()`.
   */
  rail: unknown;
  /**
   * Fraction of the visible width to advance per click. 0.9 keeps a card of
   * overlap for orientation between pages.
   */
  pageFraction?: number;
}>();

const canScrollLeft = ref(false);
const canScrollRight = ref(false);

/**
 * Unwrap the `rail` prop into its scrollable HTMLElement. Accepts a raw element
 * or a Vue component instance exposing `$el`.
 */
function resolveScrollEl(): HTMLElement | null {
  const rail = props.rail as
    | HTMLElement
    | { $el?: HTMLElement | null }
    | null
    | undefined;
  if (!rail) {
    return null;
  }
  if (rail instanceof HTMLElement) {
    return rail;
  }
  const el = rail.$el;
  return el instanceof HTMLElement ? el : null;
}

function updateEdges() {
  const el = resolveScrollEl();
  if (!el) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    return;
  }
  const maxScroll = el.scrollWidth - el.clientWidth;
  // 1px tolerance for sub-pixel rounding at the extremes.
  canScrollLeft.value = el.scrollLeft > 1;
  canScrollRight.value = el.scrollLeft < maxScroll - 1;
}

function scrollByPage(direction: 1 | -1) {
  const el = resolveScrollEl();
  if (!el) {
    return;
  }
  const amount = el.clientWidth * (props.pageFraction ?? 0.9) * direction;
  el.scrollBy({ left: amount, behavior: "smooth" });
}

let resizeObserver: ResizeObserver | null = null;
let observedEl: HTMLElement | null = null;

function attach() {
  const el = resolveScrollEl();
  if (el === observedEl) {
    updateEdges();
    return;
  }
  if (observedEl) {
    observedEl.removeEventListener("scroll", updateEdges);
    resizeObserver?.disconnect();
  }
  observedEl = el;
  if (el) {
    el.addEventListener("scroll", updateEdges, { passive: true });
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateEdges());
      resizeObserver.observe(el);
    }
  }
  updateEdges();
}

onMounted(attach);

// The rail element is created after this component mounts (it lives in a
// sibling slot), and is replaced when the virtualized list re-renders, so
// re-attach whenever the resolved ref identity changes.
watch(
  () => props.rail,
  () => attach(),
  { flush: "post" },
);

onBeforeUnmount(() => {
  observedEl?.removeEventListener("scroll", updateEdges);
  resizeObserver?.disconnect();
});

defineExpose({ updateEdges });
</script>
